import * as React from 'react'
import { connect } from 'react-redux'
import { get } from 'lodash'
import * as actions from '../../../../../actions'
import * as events from '../../../../../events'
import {
  StackChildren,
  Inset,
  Colors,
  Spacing,
  Media,
} from '@doordash/design-language'
import ListCell from '@doordash/component-list-cell'
import Separator from '@doordash/component-separator'
import { NumberField } from '@doordash/component-fields'
import Button, { IconButton } from '@doordash/component-button'
import styled from 'styled-components'

import { StripeCredentials } from '../../../../../models/types'
import t from '../../strings'
import Form from '../../../.././../utilities/Form'
import { NoBankAccountModal } from './NoBankAccountModal'
import { Stripe, HandleChange } from '../../AddBankAccount'
import * as DA from '../../../../../typeModules/events'
import { BankAccountSetup as Tag } from '../../../../../models/selectorAnchors'

const FormSeparator = styled(Separator)`
  ${Media.Mobile`
    width: 100vw;
    margin-left: -${Spacing.XSmall}px;
  `}
`

const StyledListCellMenuItem = styled.div`
  &:hover {
    cursor: pointer;
    background-color: ${Colors.ButtonFlatPrimaryBackgroundHovered};
  }
`

type USAProps = {
  handleSecondaryAction?: HandleChange<HTMLInputElement>
  onSuccess?: <T>(t?: T) => void
  track?: (
    action: DA.EventActions,
    name: DA.AddBankAccountNames,
    data?: object
  ) => Promise<void>
}

type ReduxProps = {
  stripeCredentials?: StripeCredentials
  application?: object
  createBankAccount?: (data: {
    token: string
    isW2Dasher: boolean
    paymentData?: {
      country?: string
      currency?: string
      routing_number: string
      account_number: string
      account_holder_name?: string
    }
  }) => Promise<void>
  getApplicationStatus?: (uniqueLink: string) => Promise<void>
  uniqueLink?: string
  stripe?: Stripe
  isW2Dasher?: boolean
}

export const USA: React.FC<USAProps & ReduxProps> = ({
  stripeCredentials,
  application,
  createBankAccount,
  getApplicationStatus,
  uniqueLink,
  stripe,
  isW2Dasher,
  handleSecondaryAction,
  onSuccess,
  track,
}) => {
  const [accountNumberErrors, setAccountNumberErrors] = React.useState<string>()
  const [routingNumberErrors, setRoutingNumberErrors] = React.useState<string>()
  const [isNoBankModalShowing, setIsNoBankModalShowing] = React.useState<
    boolean
  >(false)

  const fields = {
    routingNumber: '',
    accountNumber: '',
  }

  const validators = {
    accountNumber: error => (!error ? accountNumberErrors : ''),
    routingNumber: error => (!error ? routingNumberErrors : ''),
  }

  React.useEffect(() => {
    track(DA.EventActions.Rendered, DA.AddBankAccountNames.AddBankAccount)
  }, [track])

  const handleNoBankClick = (e: React.ChangeEvent) => {
    e.preventDefault()
    track(DA.EventActions.Clicked, DA.AddBankAccountNames.NoBankAccountModal)
    setIsNoBankModalShowing(true)
  }

  const setBankAccountFormErrors = () => {
    const errorMessage = t.errorMessageRoutingAccount()

    setRoutingNumberErrors(errorMessage)
    setAccountNumberErrors(errorMessage)
  }

  const handleSubmit = async data => {
    setAccountNumberErrors('')
    setRoutingNumberErrors('')

    const { accountNumber, routingNumber } = data
    const firstName = get(application, 'firstName', '')
    const lastName = get(application, 'lastName', '')
    const countryShortname = get(stripeCredentials, 'countryShortname', '')
    const currencyCode = get(stripeCredentials, 'currencyCode', '')

    if (accountNumber === '' || routingNumber === '') {
      setBankAccountFormErrors()
      return
    }

    /**
     * TODO(@anthonylim24): routing_number is passed as a string while account_number is passed as a number. This is preserving existing functionality, but more investigation is needed as to which is the intended data type.
     */
    const payload = {
      country: countryShortname,
      currency: currencyCode,
      routing_number: `${routingNumber}`,
      account_number: accountNumber,
      account_holder_name: `${firstName} ${lastName}`,
      account_holder_type: 'individual',
    }

    try {
      const bankToken = await stripe.createToken('bank_account', payload)
      if (bankToken.error) {
        throw bankToken.error
      }
      await createBankAccount({
        token: bankToken.token,
        isW2Dasher,
        paymentData: payload,
      })

      track(DA.EventActions.Success, DA.AddBankAccountNames.AddBankAccount)

      getApplicationStatus(uniqueLink)

      if (onSuccess) {
        onSuccess()
      }
    } catch (e) {
      track(
        DA.EventActions.Error,
        DA.AddBankAccountNames.AddBankAccount,
        e.message
      )
      setBankAccountFormErrors()

      if (e.type !== 'invalid_request_error') {
        // throw an error for sentry to catch
        throw e
      }
    }
  }

  return (
    <StackChildren>
      <Form onSubmit={handleSubmit} validators={validators} fields={fields}>
        {(fields, handleChange, handleSubmit, { isLoading }) => (
          <form onSubmit={handleSubmit}>
            <StackChildren size={StackChildren.Sizes.Medium}>
              <NumberField
                label={t.routingNumber()}
                onChange={value => {
                  handleChange('routingNumber', value)
                }}
                placeholder={''}
                {...fields.routingNumber}
                error={routingNumberErrors}
                data-anchor-id={Tag.RoutingNumber1}
              />

              <NumberField
                label={t.checkingAccountNumber()}
                onChange={value => {
                  handleChange('accountNumber', value)
                }}
                placeholder={''}
                {...fields.accountNumber}
                error={accountNumberErrors}
                data-anchor-id={Tag.CheckingAccountNumber1}
              />

              <FormSeparator />

              <StyledListCellMenuItem>
                <ListCell
                  title={t.noBankTitle()}
                  secondaryTitleALignment={ListCell.Alignment.Center}
                  size={ListCell.Sizes.Small}
                  iconEnd={{
                    type: ListCell.IconTypes.ChevronRight,
                    accessibilityLabel: `Chevron Right`,
                    size: IconButton.Sizes.Medium,
                    color: Colors.TextTertiary,
                  }}
                  insetSize={Inset.Sizes.Small}
                  separatorType={ListCell.SeparatorTypes.FullBorder}
                  titleMaxLines={2}
                  onClick={handleNoBankClick}
                  data-anchor-id={Tag.NoBankAccountButton}
                />
              </StyledListCellMenuItem>

              <StackChildren size={StackChildren.Sizes.XxSmall}>
                <Button
                  onClick={async () => {
                    track(
                      DA.EventActions.Submit,
                      DA.AddBankAccountNames.AddBankAccount
                    )
                    await handleSubmit()
                  }}
                  state={
                    isLoading ? Button.States.Loading : Button.States.Default
                  }
                  data-anchor-id={Tag.PrimaryAction}
                >
                  {t.submit()}
                </Button>
                {handleSecondaryAction && (
                  <Button
                    type={Button.Types.Tertiary}
                    onClick={e => {
                      track(
                        DA.EventActions.Clicked,
                        DA.AddBankAccountNames.AddLater
                      )
                      handleSecondaryAction(e)
                    }}
                    data-anchor-id={Tag.SecondaryAction}
                  >
                    {t.addLater()}
                  </Button>
                )}
              </StackChildren>
            </StackChildren>
          </form>
        )}
      </Form>

      {isNoBankModalShowing && (
        <NoBankAccountModal
          onClose={() => {
            track(
              DA.EventActions.Close,
              DA.AddBankAccountNames.NoBankAccountModal
            )
            setIsNoBankModalShowing(false)
          }}
        />
      )}
    </StackChildren>
  )
}

const mapStateToProps = ({ application, stripeCredentials, experiments }) => ({
  application,
  stripeCredentials,
  experiments,
})

const mapDispatchToProps = {
  getApplicationStatus: actions.getApplicationStatus,
  createBankAccount: actions.createBankAccount,
  track: events.trackAddBankAccountPage,
}

export default connect(mapStateToProps, mapDispatchToProps)(USA)
